import React, { Fragment } from 'react'
import omit from 'lodash/omit'
import { type useForm } from 'react-hook-form'
import { css } from '@emotion/react'
import { spacing, FormFieldError } from '@retailer-platform/shared-components'
import { InputBase } from '@instacart/ids-core'
import { useDashIntl } from 'intl/intl.hooks'
import { type DashMessages } from 'intl/intl.types'
import { Input } from 'gin-and-tonic/common/input/Input'
import { useFormErrorMessageMap } from 'utils/react-hook-form/reactHookForm.hooks'

const outerStyles = css({
  width: 'auto',
  margin: 0,
  marginBottom: spacing.X12,
})

type ErrorRecordType = ReturnType<typeof useForm>['errors']

interface Props {
  disabled: boolean
  name: string
  errors: ErrorRecordType
  errorMessages: Record<string, DashMessages>
  labelMessageId: DashMessages
  useNewStyle?: boolean
  hidden?: boolean
}

export const AccountFormInput = React.forwardRef<HTMLInputElement, Props>(
  ({ name, errors, errorMessages, labelMessageId, useNewStyle, ...props }, ref) => {
    const intl = useDashIntl()

    const fieldName = intl.formatMessage({ id: labelMessageId })
    const errorMessageId = useFormErrorMessageMap(errorMessages)(errors[name])

    const formattedMessage =
      errorMessageId && intl.formatMessage({ id: errorMessageId }, { fieldName })

    const testId = `accountFormInput-${name}`
    return useNewStyle ? (
      <Fragment>
        <InputBase
          {...omit(props, 'styles')}
          ref={ref}
          id={name}
          name={name}
          placeholder={fieldName}
          data-testid={testId}
          css={{ maxHeight: '38px' }}
          autoComplete="on"
          hidden={props.hidden}
        />
        {formattedMessage && (
          <FormFieldError data-testid={`${testId}-error-message`}>
            {formattedMessage}
          </FormFieldError>
        )}
      </Fragment>
    ) : (
      <Input
        {...props}
        outerStyles={outerStyles}
        ref={ref}
        name={name}
        placeholder={fieldName}
        errorMessage={formattedMessage}
        data-testid={`accountFormInput-${name}`}
        hidden={props.hidden}
      />
    )
  }
)
